<script setup lang="ts">
import { ref, watch } from 'vue';
import { GpxDateTimePicker } from '@gpx-spa/ui';
import Message from 'primevue/message';
import type { ShipmentFormStopRow } from '@/stores/useShipmentFormStore';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import type { ShipmentFormStopFieldErrors } from '@/components/shipment/form/utils/shipment-form-stop-validation';

type PlannedTimeField = 'plannedDeparture' | 'plannedArrival';
type StopRole = 'origin' | 'waypoint' | 'destination';

const props = defineProps<{
  stop: ShipmentFormStopRow;
  cardRole: StopRole;
  fieldErrors?: ShipmentFormStopFieldErrors;
  suppressPlannedTimeManualMark?: boolean;
  departureDisabled: boolean;
  arrivalDisabled: boolean;
}>();

const emit = defineEmits<{
  plannedTimeUserEdit: [field: PlannedTimeField, value: Date | null];
}>();

const store = useShipmentFormStore();

const draftPlannedDeparture = ref<Date | null>(null);
const draftPlannedArrival = ref<Date | null>(null);
const departureOverlayOpen = ref(false);
const arrivalOverlayOpen = ref(false);

function samePlannedInstant(a: Date | null, b: Date | null): boolean {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return a.getTime() === b.getTime();
}

watch(
  () => props.stop.plannedDeparture,
  (v) => {
    if (!departureOverlayOpen.value) {
      draftPlannedDeparture.value = v ?? null;
    }
  },
  { immediate: true },
);

watch(
  () => props.stop.plannedArrival,
  (v) => {
    if (!arrivalOverlayOpen.value) {
      draftPlannedArrival.value = v ?? null;
    }
  },
  { immediate: true },
);

watch(
  () => props.stop.key,
  () => {
    departureOverlayOpen.value = false;
    arrivalOverlayOpen.value = false;
    draftPlannedDeparture.value = props.stop.plannedDeparture ?? null;
    draftPlannedArrival.value = props.stop.plannedArrival ?? null;
  },
);

function commitPlannedDepartureIfChanged() {
  const v = draftPlannedDeparture.value;
  if (samePlannedInstant(v, props.stop.plannedDeparture)) {
    return;
  }
  if (props.suppressPlannedTimeManualMark) {
    store.setStopPlannedDeparture(props.stop.key, v);
    return;
  }
  emit('plannedTimeUserEdit', 'plannedDeparture', v);
  store.setStopPlannedDeparture(props.stop.key, v);
}

function commitPlannedArrivalIfChanged() {
  const v = draftPlannedArrival.value;
  if (samePlannedInstant(v, props.stop.plannedArrival)) {
    return;
  }
  if (props.suppressPlannedTimeManualMark) {
    store.setStopPlannedArrival(props.stop.key, v);
    return;
  }
  emit('plannedTimeUserEdit', 'plannedArrival', v);
  store.setStopPlannedArrival(props.stop.key, v);
}
</script>

<template>
  <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
    <div v-if="cardRole !== 'destination'">
      <label class="mb-1 block text-sm font-medium text-gpx_gray-100"
        >Planned departure</label
      >
      <GpxDateTimePicker
        v-model="draftPlannedDeparture"
        hour-format="12"
        size="small"
        fluid
        show-clear
        show-icon
        icon-display="input"
        date-format="yy-mm-dd"
        placeholder="Select departure time"
        :invalid="Boolean(fieldErrors?.plannedDeparture)"
        :disabled="departureDisabled"
        @show="departureOverlayOpen = true"
        @hide="
          () => {
            departureOverlayOpen = false;
            commitPlannedDepartureIfChanged();
          }
        "
        @clear-click="commitPlannedDepartureIfChanged"
      />
      <Message
        v-if="fieldErrors?.plannedDeparture"
        severity="error"
        size="small"
        variant="simple"
        class="mt-1"
        :closable="false"
      >
        {{ fieldErrors.plannedDeparture }}
      </Message>
    </div>
    <div v-if="cardRole !== 'origin'">
      <label class="mb-1 block text-sm font-medium text-gpx_gray-100"
        >Planned arrival</label
      >
      <GpxDateTimePicker
        v-model="draftPlannedArrival"
        hour-format="12"
        size="small"
        fluid
        show-clear
        show-icon
        icon-display="input"
        date-format="yy-mm-dd"
        placeholder="Select arrival time"
        :invalid="Boolean(fieldErrors?.plannedArrival)"
        :disabled="arrivalDisabled"
        @show="arrivalOverlayOpen = true"
        @hide="
          () => {
            arrivalOverlayOpen = false;
            commitPlannedArrivalIfChanged();
          }
        "
        @clear-click="commitPlannedArrivalIfChanged"
      />
      <Message
        v-if="fieldErrors?.plannedArrival"
        severity="error"
        size="small"
        variant="simple"
        class="mt-1"
        :closable="false"
      >
        {{ fieldErrors.plannedArrival }}
      </Message>
    </div>
  </div>
</template>
